<template>
    <div class="node" v-bind:class="{gridRow: GridRows}" v-if="IsVisible">
        <div class="node-main" v-bind:class="{selected: IsSelected}" :style="RowIndentStyle" @click.stop="Value_Click()">
            <div class="expander" v-bind:class="{expanded: IsExpanded, collapsed: !IsExpanded, hidden: !HasChildren}" @click.stop="Expander_Click" />
            <span v-if="GridRows" class="theme-svg" :class="HasChildren ? 'nonleafnode' : 'leafnode'"></span>
            <span class="label" v-bind:class="{disabled: DisplayOnly, highlightBlue: GridRows && IsSearchMatch, highlightYellow: GridRows && IsCurrentMatch}">{{ShownLabel}}</span>
        </div>

        <div class="children" v-if="ShowChildren">
            <TreeParameterNode v-for="child, i in Children"
                               v-bind:key="child.Value.key || i"
                               :Definition="child"
                               :SearchText="SearchText"
                               :AutoExpand="AutoExpand"
                               :ShowOnlyMatches="ShowOnlyMatches"
                               :MatchNameAndCaption="MatchNameAndCaption"
                               :DisplayMode="DisplayMode"
                               :HighlightedKey="HighlightedKey"
                               :GridRows="GridRows" />
        </div>
    </div>
</template>

<script lang="ts">
    import { Component, Prop, Vue, Watch } from 'vue-facing-decorator';
    import { TreeParameterNodeDefinition as NodeDefinition } from "../../../api/Extensions";
    import * as api from "../../../api/ApiClient";

    @Component({ name: "TreeParameterNode" })
    export default class TreeParameterNode extends Vue {
        @Prop() private Definition!: NodeDefinition;
        @Prop({ default: "" }) private SearchText!: string;
        @Prop({ default: false }) private AutoExpand!: boolean;
        @Prop({ default: true }) private ShowOnlyMatches!: boolean;
        @Prop({ default: false }) private MatchNameAndCaption!: boolean;
        @Prop({ default: "" }) private DisplayMode!: string;
        @Prop({ default: "" }) private HighlightedKey!: string;
        @Prop({ default: false }) private GridRows!: boolean;

        get Value(): api.FormParameterValue {
            return this.Definition.Value;
        }
        get Label(): string {
            return this.Definition.Label;
        }
        get ShownLabel(): string {
            if (this.DisplayMode === "name")
                return this.Value.name || "";
            if (this.DisplayMode === "caption")
                return this.Value.caption || "";
            if (this.DisplayMode === "nameAndCaption") {
                const name = this.Value.name || "";
                const caption = this.Value.caption || "";
                if (!name)
                    return caption;
                if (!caption)
                    return name;
                return `${name} - ${caption}`;
            }
            return this.Label;
        }
        get Parent(): NodeDefinition | null {
            return this.Definition.Parent;
        }
        get Children(): NodeDefinition[] {
            return this.Definition.Children;
        }
        get DisplayOnly(): boolean {
            return this.Value.displayOnly;
        }

        get RowIndentStyle(): Record<string, string> | undefined {
            if (!this.GridRows)
                return undefined;
            let depth = 0;
            for (let parent = this.Parent; parent; parent = parent.Parent)
                depth++;
            return { paddingLeft: `${depth * 28}px` };
        }

        private IsExpanded = false;

        get IsSelected(): boolean {
            return this.Value == this.$store.state.forms.SelectedTreeParameterValue;
        }

        get HasChildren(): boolean {
            return this.Children.length > 0;
        }

        get HasSearchText(): boolean {
            return this.NormalizedSearchText.length > 0;
        }

        get IsVisible(): boolean {
            if (!this.HasSearchText || !this.ShowOnlyMatches) {
                return true;
            }

            return this.MatchesSearchText || this.HasMatchingDescendant;
        }

        get IsSearchMatch(): boolean {
            return this.HasSearchText && this.MatchesSearchText && !this.IsCurrentMatch;
        }

        get IsCurrentMatch(): boolean {
            return this.HighlightedKey !== "" && this.Value.key === this.HighlightedKey;
        }

        get ShowChildren(): boolean {
            return this.IsExpanded;
        }

        private get NormalizedSearchText(): string {
            return this.SearchText.trim().toLocaleLowerCase();
        }

        private get MatchesSearchText(): boolean {
            if (this.MatchNameAndCaption) {
                return this.textIncludes(this.Value.name) || this.textIncludes(this.Value.caption);
            }
            return (this.Label || "").toLocaleLowerCase().includes(this.NormalizedSearchText);
        }

        private textIncludes(value: string | null | undefined): boolean {
            return (value || "").toLocaleLowerCase().includes(this.NormalizedSearchText);
        }

        private get HasMatchingDescendant(): boolean {
            if (!this.HasSearchText) {
                return false;
            }

            return this.Children.some(child => this.NodeMatchesSearch(child, this.NormalizedSearchText));
        }

        private NodeMatchesSearch(node: NodeDefinition, searchText: string): boolean {
            if (this.nodeSelfMatches(node, searchText)) {
                return true;
            }

            return node.Children.some(child => this.NodeMatchesSearch(child, searchText));
        }

        private nodeSelfMatches(node: NodeDefinition, searchText: string): boolean {
            if (this.MatchNameAndCaption) {
                const name = (node.Value.name || "").toLocaleLowerCase();
                const caption = (node.Value.caption || "").toLocaleLowerCase();
                return name.includes(searchText) || caption.includes(searchText);
            }
            return (node.Label || "").toLocaleLowerCase().includes(searchText);
        }

        private wasAutoExpanded = false;
        private expandedForHighlight = false;

        created() {
            this.syncSearchExpansion();
            this.syncHighlightExpansion();
        }

        mounted() {
            this.scrollCurrentMatchIntoView();
        }

        @Watch("SearchText")
        @Watch("AutoExpand")
        onSearchExpansionChanged() {
            this.syncSearchExpansion();
        }

        private syncSearchExpansion() {
            if (this.AutoExpand && this.HasSearchText && this.HasMatchingDescendant) {
                if (!this.IsExpanded) {
                    this.IsExpanded = true;
                    this.wasAutoExpanded = true;
                }
            } else if (this.wasAutoExpanded) {
                this.IsExpanded = false;
                this.wasAutoExpanded = false;
            }
        }

        @Watch("HighlightedKey")
        onHighlightedKeyChanged() {
            this.syncHighlightExpansion();
            this.scrollCurrentMatchIntoView();
        }

        private syncHighlightExpansion() {
            if (this.HasHighlightedDescendant) {
                if (!this.IsExpanded) {
                    this.IsExpanded = true;
                    this.expandedForHighlight = true;
                }
            } else if (this.expandedForHighlight) {
                this.IsExpanded = false;
                this.expandedForHighlight = false;
            }
        }

        private scrollCurrentMatchIntoView() {
            if (!this.IsCurrentMatch) {
                return;
            }
            this.$nextTick(() => {
                this.scrollMatchRow();
                requestAnimationFrame(() => {
                    this.scrollMatchRow();
                    requestAnimationFrame(() => this.scrollMatchRow());
                });
            });
        }

        private scrollMatchRow() {
            const root = this.$el;
            if (!(root instanceof HTMLElement)) {
                return;
            }
            const row = root.querySelector(":scope > .node-main");
            const element = row instanceof HTMLElement ? row : root;
            const container = element.closest(".showStriped");
            if (!(container instanceof HTMLElement)) {
                element.scrollIntoView?.({ block: "start" });
                return;
            }
            const top = element.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
            container.scrollTop = Math.max(0, top - 8);
        }

        private get HasHighlightedDescendant(): boolean {
            if (!this.HighlightedKey) {
                return false;
            }
            return this.Children.some(child => this.nodeOrDescendantHasKey(child, this.HighlightedKey));
        }

        private nodeOrDescendantHasKey(node: NodeDefinition, key: string): boolean {
            if (node.Value.key === key) {
                return true;
            }
            return node.Children.some(child => this.nodeOrDescendantHasKey(child, key));
        }

        Value_Click() {
            if (!this.DisplayOnly)
                this.$store.commit("forms/SetSelectedTreeParameterValue", this.Value);
        }

        Expander_Click() {
            this.IsExpanded = !this.IsExpanded;
            this.wasAutoExpanded = false;
            this.expandedForHighlight = false;
        }
    }
</script>

<style scoped>
    .node {
        padding-bottom: 0px;
    }

    .node-main {
        padding-top: 5px;
        padding-bottom: 5px;
        line-height: 28px;
        height: 36px;
        position: relative;
        white-space: nowrap;
    }

        .node-main.selected {
            background-color: var(--accent-color);
            color: var(--accent-foreground);
        }

        .node-main .label {
            overflow: hidden;
            position: absolute;
            left: 56px;
            right: 28px;
            text-overflow: ellipsis;
            cursor: pointer;
            user-select: none;
        }

            .node-main .label.disabled {
                opacity: 0.5;
                cursor: default;
            }

        .expander.hidden {
            visibility: hidden;
        }

    .children {
        padding-left: 20px;
    }

    .gridRow > .node-main {
        display: flex;
        align-items: center;
        box-sizing: border-box;
        height: 42px;
        padding-top: 0;
        padding-bottom: 0;
        line-height: 41px;
    }

        .gridRow > .node-main:hover {
            background-color: var(--row-hover);
        }

        .gridRow > .node-main.selected {
            background-color: var(--accent-color4);
            box-shadow: inset 0 0 0 1px #2196f3;
            color: inherit;
        }

        .gridRow > .node-main .label {
            position: static;
            flex: 0 1 auto;
            min-width: 0;
            line-height: 20px;
        }

            .gridRow > .node-main .label.highlightBlue {
                background-color: #cce4fc;
            }

            .gridRow > .node-main .label.highlightYellow {
                background-color: #ffff55;
            }

    .gridRow > .children {
        padding-left: 0;
    }

    .leafnode,
    .nonleafnode {
        flex: 0 0 auto;
        width: 24px;
        height: 24px;
        margin-left: 3px;
        margin-right: 6px;
        background-repeat: no-repeat;
    }

    .leafnode {
        background-image: url("../../../../public/icons/models/leaf_node.svg");
    }

    .nonleafnode {
        background-image: url("../../../../public/icons/models/node-1.svg");
    }
</style>
